import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import config from './config';
import { getAircraftModex } from './aircraftModex';

// ---------------------------------------------------------------------------
// TV Dashboard - public, login-free aircraft status display.
//
// Authenticated by a per-device token. The first visit may include
// ?token=<TOKEN>, which is exchanged for an HttpOnly cookie and then removed
// from the address bar. Later polls send only that cookie.
//
// Tokens are created in Django Admin -> "TV Dashboard Tokens". Each token can
// optionally be scoped to a single Site so a TV at one location only shows that site.
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = ['UP', 'XC', 'DN', 'DS', 'PM', 'FCF', 'AWP', 'OTH', 'DET', 'AIMD', 'ACI', 'REI'];
const MCRS_STATUSES = ['UP', 'XC', 'DN', 'DS', 'PM', 'FCF'];
const OOR_STATUSES = ['AWP', 'OTH', 'AIMD', 'ACI', 'REI'];

const STATUS_COLORS = {
    UP: '#28a745',
    XC: '#20c997',
    DN: '#dc3545',
    DS: '#dc3545',
    PM: '#ffc107',
    FCF: '#8716bb',
    AWP: '#335d80',
    OTH: '#6c757d',
    DET: '#6c757d',
    AIMD: '#6c757d',
    ACI: '#6c757d',
    REI: '#6c757d',
};

// All sizes are expressed in `vmin` units so they scale automatically with
// whichever screen dimension is smaller. On a vertical 55" 1080p TV that's
// the width (1080), so 1vmin = 10.8px.
//
// `fs` is the URL-driven multiplier (?fontScale=N), default 1.
//
// `cols` is the number of columns the aircraft tile grid uses (fixed once
// at mount).
//
// `theme` is 'dark' or 'light'.
const themePalette = (theme) => {
    const isDark = theme === 'dark';
    return {
        isDark,
        pageBg: isDark ? '#0b0d10' : '#f4f6f8',
        text: isDark ? '#f0f3f6' : '#1f2933',
        muted: isDark ? '#9aa6b2' : '#5a6470',
        border: isDark ? '#1f2933' : '#dee2e6',
        cardBg: isDark ? '#161a20' : '#ffffff',
        gridBg: isDark ? '#161a20' : '#ffffff',
        tileBg: isDark ? '#1f2933' : '#f0f3f6',
        tileBorder: isDark ? '#2c3641' : '#cdd5dd',
        cardShadow: isDark ? '0 0.2vmin 0.8vmin rgba(0,0,0,0.4)' : '0 0.2vmin 0.8vmin rgba(0,0,0,0.08)',
        toggleBg: isDark ? '#1f2933' : '#ffffff',
        toggleBorder: isDark ? '#2c3641' : '#cdd5dd',
        toggleText: isDark ? '#f0f3f6' : '#1f2933',
    };
};

const buildStyles = (fs = 1, cols = 6, theme = 'dark') => {
    const p = themePalette(theme);
    return {
        page: {
            backgroundColor: p.pageBg,
            color: p.text,
            minHeight: '100vh',
            padding: '1.6vmin 2vmin',
            fontFamily: 'sans-serif',
            boxSizing: 'border-box',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.6vmin',
            paddingBottom: '1vmin',
            borderBottom: `0.2vmin solid ${p.border}`,
        },
        headerLeft: { display: 'flex', flexDirection: 'column', gap: '0.3vmin' },
        title: { fontSize: `${5.4 * fs}vmin`, margin: 0, fontWeight: 700, letterSpacing: '0.05vmin' },
        subtitle: { fontSize: `${2.4 * fs}vmin`, color: p.muted, fontWeight: 500 },
        headerRight: { display: 'flex', alignItems: 'center', gap: '2vmin' },
        clock: { fontSize: `${5.4 * fs}vmin`, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
        liveBadge: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.8vmin',
            fontSize: `${2.4 * fs}vmin`,
            fontWeight: 700,
            padding: '0.6vmin 1.4vmin',
            borderRadius: '999px',
        },
        liveDot: {
            width: '1.2vmin',
            height: '1.2vmin',
            borderRadius: '50%',
            animation: 'tvDashPulse 2s infinite',
        },
        themeToggle: {
            cursor: 'pointer',
            fontSize: `${2.2 * fs}vmin`,
            fontWeight: 700,
            padding: '0.6vmin 1.4vmin',
            borderRadius: '999px',
            backgroundColor: p.toggleBg,
            color: p.toggleText,
            border: `0.15vmin solid ${p.toggleBorder}`,
            fontFamily: 'inherit',
        },
        summaryRow: {
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '1.6vmin',
            marginBottom: 0,
        },
        summaryCard: {
            backgroundColor: p.cardBg,
            borderRadius: '1vmin',
            padding: '1.8vmin 2vmin',
            boxShadow: p.cardShadow,
        },
        summaryTitle: { fontSize: `${3 * fs}vmin`, fontWeight: 700, marginBottom: '1.2vmin' },
        summaryGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1.2vmin',
        },
        summaryItem: { textAlign: 'center' },
        summaryNumber: { fontSize: `${5 * fs}vmin`, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
        summaryPct: { fontSize: `${2.2 * fs}vmin`, color: p.muted, fontWeight: 600 },
        summaryLabel: { fontSize: `${2 * fs}vmin`, color: p.muted, marginTop: '0.4vmin', letterSpacing: '0.05vmin' },
        statusSection: {
            marginBottom: '1.2vmin',
            borderRadius: '0.8vmin',
            overflow: 'hidden',
            boxShadow: p.cardShadow,
        },
        statusHeader: {
            padding: '1.2vmin 1.8vmin',
            color: '#fff',
            fontWeight: 700,
            fontSize: `${2.8 * fs}vmin`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        statusHeaderCollapsed: {
            padding: '0.6vmin 1.8vmin',
            color: '#fff',
            fontWeight: 700,
            fontSize: `${2 * fs}vmin`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            opacity: 0.75,
        },
        statusHeaderLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.8vmin',
        },
        caret: {
            display: 'inline-block',
            fontSize: `${2 * fs}vmin`,
            opacity: 0.85,
        },
        aircraftGrid: {
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: '0.8vmin',
            padding: '1.2vmin 1.6vmin',
            backgroundColor: p.gridBg,
        },
        aircraftItem: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.6vmin 0.3vmin',
            backgroundColor: p.tileBg,
            borderRadius: '0.4vmin',
            border: `0.15vmin solid ${p.tileBorder}`,
            textAlign: 'center',
            color: p.text,
        },
        buno: { fontSize: `${3.6 * fs}vmin`, fontWeight: 700, lineHeight: 1, marginBottom: '0.2vmin', letterSpacing: '0.05vmin' },
        lfd: { fontSize: `${1.6 * fs}vmin`, color: p.muted, lineHeight: 1.1 },
        noAircraft: { textAlign: 'center', color: p.muted, fontStyle: 'italic', padding: '1.6vmin', fontSize: `${2 * fs}vmin` },
        fatalBox: {
            maxWidth: '720px',
            margin: '15vh auto',
            backgroundColor: p.cardBg,
            border: `1px solid ${p.tileBorder}`,
            borderRadius: '12px',
            padding: '32px',
            textAlign: 'center',
        },
        fatalTitle: { fontSize: '28px', fontWeight: 700, marginBottom: '12px' },
        fatalBody: { fontSize: '18px', color: p.muted, lineHeight: 1.6 },
        code: {
            fontFamily: 'monospace',
            backgroundColor: p.pageBg,
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '16px',
        },
        // Theme-dependent overrides used by aircraftItemStyle(): pale tints
        // for light mode so the "days since LFD" signal stays vivid against
        // a white tile background; dark warm/cool fills for dark mode.
        _lfdGt90: p.isDark
            ? { backgroundColor: '#2a0e3a', borderColor: '#9c27b0', color: '#fff' }
            : { backgroundColor: '#f3e5f5', borderColor: '#9c27b0', color: '#4a148c' },
        _lfdGt60: p.isDark
            ? { backgroundColor: '#3b1414', borderColor: '#f44336', color: '#fff' }
            : { backgroundColor: '#ffebee', borderColor: '#f44336', color: '#7f1d1d' },
        _lfdGt30: p.isDark
            ? { backgroundColor: '#3a2a0d', borderColor: '#ff9800', color: '#fff' }
            : { backgroundColor: '#fff8e1', borderColor: '#ff9800', color: '#7c4a03' },
    };
};

// Default styles for the fatal-error / loading screens (uses dark theme).
const fatalStyles = buildStyles(1, 6, 'dark');

const pulseKeyframes = `
@keyframes tvDashPulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
    100% { opacity: 1; transform: scale(1); }
}`;

if (typeof document !== 'undefined' && !document.getElementById('tv-dashboard-keyframes')) {
    const style = document.createElement('style');
    style.id = 'tv-dashboard-keyframes';
    style.textContent = pulseKeyframes;
    document.head.appendChild(style);
}

const REFRESH_INTERVAL_MS = 30 * 1000;

const formatLfd = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const dateParts = dateString.split('T')[0].split('-');
        if (dateParts.length === 3) {
            const year = parseInt(dateParts[0], 10);
            const month = parseInt(dateParts[1], 10) - 1;
            const day = parseInt(dateParts[2], 10);
            return new Date(year, month, day).toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: '2-digit',
            });
        }
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short', day: '2-digit', year: '2-digit',
        });
    } catch {
        return 'N/A';
    }
};

const daysSinceFly = (dateString) => {
    if (!dateString) return null;
    const dateParts = dateString.split('T')[0].split('-');
    let flyDate;
    if (dateParts.length === 3) {
        flyDate = new Date(
            parseInt(dateParts[0], 10),
            parseInt(dateParts[1], 10) - 1,
            parseInt(dateParts[2], 10),
        );
    } else {
        flyDate = new Date(dateString);
    }
    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const flyMid = new Date(flyDate.getFullYear(), flyDate.getMonth(), flyDate.getDate());
    return Math.floor((todayMid - flyMid) / (1000 * 60 * 60 * 24));
};

const aircraftItemStyle = (aircraft, styles) => {
    const days = daysSinceFly(aircraft.last_fly_date);
    if (days === null) return styles.aircraftItem;
    if (days > 90) return { ...styles.aircraftItem, ...styles._lfdGt90 };
    if (days > 60) return { ...styles.aircraftItem, ...styles._lfdGt60 };
    if (days > 30) return { ...styles.aircraftItem, ...styles._lfdGt30 };
    return styles.aircraftItem;
};

function getApiBaseURL() {
    const envBase = process.env.REACT_APP_API_BASE_URL || config.API_BASE_URL || '';
    let base = (envBase || '').trim().replace(/\/+$/, '');
    if (!base) return '/api';
    if (!/\/(api)$/.test(base)) base += '/api';
    return base;
}

function TVDashboardPage() {
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const token = params.get('token');

    // ?fontScale=N — multiplies every font size in `buildStyles` before the
    // auto-fit shrinks to viewport. Default 1. Useful when a particular TV
    // looks slightly too big/small after the auto-fit.
    const fontScale = useMemo(() => {
        const raw = params.get('fontScale');
        if (!raw) return 1;
        const n = parseFloat(raw);
        return Number.isFinite(n) && n > 0.25 && n < 4 ? n : 1;
    }, [params]);

    const [data, setData] = useState({ device_name: '', site: null, aircraft: [], generated_at: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [credentialReady, setCredentialReady] = useState(false);
    const [now, setNow] = useState(new Date());
    const [lastUpdated, setLastUpdated] = useState(null);
    const failuresRef = useRef(0);

    // Theme: 'dark' or 'light'. Persisted per-browser under its own key so
    // the TV's preference doesn't fight with the main app's theme on the
    // same machine. Defaults to 'dark' (the original TV look).
    const [theme, setTheme] = useState(() => {
        if (typeof window === 'undefined') return 'dark';
        return window.localStorage.getItem('tv-dashboard-theme') || 'dark';
    });
    const toggleTheme = useCallback(() => {
        setTheme((prev) => {
            const next = prev === 'dark' ? 'light' : 'dark';
            try {
                window.localStorage.setItem('tv-dashboard-theme', next);
            } catch {
                // localStorage may be unavailable (private mode, etc.) — ignore.
            }
            return next;
        });
    }, []);

    // Decide grid column count ONCE at mount based on orientation. Portrait
    // (vertical TV) gets a higher-density tile grid; landscape gets even
    // more columns so the grid fills the wider screen. Fixing the column
    // count at mount avoids any "grid reflows as I auto-fit and now the
    // page wants a new scale" feedback loop.
    const gridCols = useMemo(() => {
        if (typeof window === 'undefined') return 8;
        const portrait = window.innerHeight >= window.innerWidth;
        return portrait ? 8 : 14;
    }, []);

    const styles = useMemo(
        () => buildStyles(fontScale, gridCols, theme),
        [fontScale, gridCols, theme],
    );

    // Stable one-shot auto-fit:
    //   * Measures the rendered scrollHeight once per data / viewport change.
    //   * Applies a CSS transform: scale(N) so the page never overflows
    //     vertically on the TV.
    //   * Does NOT touch width — column count is fixed, so the layout
    //     doesn't reflow when scale changes. scrollHeight is invariant under
    //     CSS transforms, so a single measurement is sufficient — no
    //     feedback loop, no progressive shrinking.
    const contentRef = useRef(null);
    const [scale, setScale] = useState(1);

    const fetchData = useCallback(async () => {
        if (!credentialReady) return;
        try {
            const response = await axios.get(
                `${getApiBaseURL()}/tv-dashboard/aircraft-status/`,
                { withCredentials: true },
            );
            setData(response.data);
            setLastUpdated(new Date());
            setError(null);
            failuresRef.current = 0;
        } catch (err) {
            failuresRef.current += 1;
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                setError({ kind: 'auth', message: 'Token rejected. Check the URL or generate a new token in Django admin.' });
            } else if (failuresRef.current >= 3) {
                setError({ kind: 'network', message: 'Lost connection to the server. Retrying…' });
            }
        } finally {
            setLoading(false);
        }
    }, [credentialReady]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (token) {
                try {
                    await axios.post(
                        `${getApiBaseURL()}/tv-dashboard/activate/`,
                        { token },
                        { withCredentials: true },
                    );
                    const url = new URL(window.location.href);
                    url.searchParams.delete('token');
                    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
                } catch (err) {
                    if (!cancelled) {
                        setError({
                            kind: 'auth',
                            message: 'Token rejected. Check the URL or generate a new token in Django admin.',
                        });
                        setLoading(false);
                    }
                    return;
                }
            }
            if (!cancelled) {
                setCredentialReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    useEffect(() => {
        if (!credentialReady) {
            return undefined;
        }
        fetchData();
        const id = setInterval(fetchData, REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [credentialReady, fetchData]);

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    useLayoutEffect(() => {
        let cancelled = false;
        const measure = () => {
            if (cancelled) return;
            const el = contentRef.current;
            if (!el) return;
            const naturalH = el.scrollHeight;
            if (!naturalH) return;
            const next = Math.min(1, window.innerHeight / naturalH);
            // Only update if the change is meaningful, to avoid React doing a
            // pointless re-render for sub-pixel drift.
            setScale((prev) => (Math.abs(prev - next) > 0.005 ? next : prev));
        };
        measure();
        const onResize = () => measure();
        window.addEventListener('resize', onResize);
        // One re-measure after fonts/images have settled.
        const id = setTimeout(measure, 300);
        return () => {
            cancelled = true;
            window.removeEventListener('resize', onResize);
            clearTimeout(id);
        };
        // Deliberately omit `scale` from deps — including it would create
        // the very feedback loop this is designed to avoid.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, fontScale, gridCols, theme]);

    if (error && error.kind === 'auth') {
        return (
            <div style={fatalStyles.page}>
                <div style={fatalStyles.fatalBox}>
                    <div style={fatalStyles.fatalTitle}>TV Dashboard</div>
                    <div style={fatalStyles.fatalBody}>
                        {error.message} Open Django admin → <em>TV Dashboard Tokens</em>, then load this page once as{' '}
                        <span style={fatalStyles.code}>/tv-dashboard?token=&lt;TOKEN&gt;</span>.
                        The token is exchanged for a device cookie and removed from the address bar.
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={fatalStyles.page}>
                <div style={fatalStyles.fatalBox}>
                    <div style={fatalStyles.fatalTitle}>Loading TV Dashboard…</div>
                </div>
            </div>
        );
    }

    if (error && error.kind === 'auth') {
        return (
            <div style={fatalStyles.page}>
                <div style={fatalStyles.fatalBox}>
                    <div style={fatalStyles.fatalTitle}>Token rejected</div>
                    <div style={fatalStyles.fatalBody}>
                        {error.message}
                    </div>
                </div>
            </div>
        );
    }

    const aircraftList = Array.isArray(data.aircraft) ? data.aircraft : [];

    const grouped = aircraftList.reduce((acc, ac) => {
        const s = ac.status || 'UNKNOWN';
        if (!acc[s]) acc[s] = [];
        acc[s].push(ac);
        return acc;
    }, {});

    const totalAircraft = aircraftList.length;
    const statusTotals = STATUS_OPTIONS.reduce((acc, s) => {
        acc[s] = grouped[s]?.length || 0;
        return acc;
    }, {});
    const mcrsTotal = MCRS_STATUSES.reduce((sum, s) => sum + (statusTotals[s] || 0), 0);
    const oorTotal = OOR_STATUSES.reduce((sum, s) => sum + (statusTotals[s] || 0), 0);
    const xcTotal = statusTotals.XC || 0;
    // TAR = 75% of MCRS, minus the cross-country birds that aren't on station.
    const tarTotal = Math.max(0, Math.floor(mcrsTotal * 0.75) - xcTotal);

    const liveOk = !error;

    // Auto-fit wrapper.
    //   * Outer: fills the viewport, hides any overflow, centers the inner
    //     page horizontally.
    //   * Inner: when scale < 1, its layout width is *inverse-scaled* so that
    //     after the transform shrinks it visually, it still spans the full
    //     viewport horizontally — no side bands.
    //
    //   Why is this safe now (vs. the earlier shrink-loop)? The grid uses a
    //   FIXED column count and all sizes are in `vmin` (which is anchored to
    //   the browser viewport, not to the wrapper). So scrollHeight is
    //   completely independent of the wrapper's CSS width — measuring once,
    //   choosing a scale, and inflating the width does NOT cause the grid to
    //   reflow into more/fewer rows, so there is no feedback to loop on.
    const widthCss = scale < 0.999 ? `${100 / scale}vw` : '100vw';
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                overflow: 'hidden',
                backgroundColor: styles.page.backgroundColor,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
            }}
        >
        <div
            ref={contentRef}
            style={{
                ...styles.page,
                width: widthCss,
                transform: `scale(${scale})`,
                transformOrigin: 'top center',
                flex: '0 0 auto',
            }}
        >
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <h1 style={styles.title}>Aircraft Status</h1>
                    <div style={styles.subtitle}>
                        {data.device_name || 'TV Dashboard'}
                        {data.site ? ` · ${data.site}` : ' · All Sites'}
                        {lastUpdated && ` · Updated ${lastUpdated.toLocaleTimeString()}`}
                    </div>
                </div>
                <div style={styles.headerRight}>
                    <div
                        style={{
                            ...styles.liveBadge,
                            backgroundColor: liveOk ? 'rgba(40,167,69,0.15)' : 'rgba(220,53,69,0.15)',
                            color: liveOk ? '#28a745' : '#dc3545',
                            border: `1px solid ${liveOk ? '#28a745' : '#dc3545'}`,
                        }}
                    >
                        <span
                            style={{
                                ...styles.liveDot,
                                backgroundColor: liveOk ? '#28a745' : '#dc3545',
                            }}
                        />
                        {liveOk ? 'LIVE' : 'OFFLINE'}
                    </div>
                    <button
                        type="button"
                        onClick={toggleTheme}
                        style={styles.themeToggle}
                        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                    >
                        {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
                    </button>
                    <div style={styles.clock}>{now.toLocaleTimeString()}</div>
                </div>
            </div>

            {STATUS_OPTIONS.filter((s) => s !== 'DET').concat(['DET']).map((status) => {
                const list = grouped[status] || [];
                const count = list.length;
                const pct = totalAircraft > 0 ? ((count / totalAircraft) * 100).toFixed(1) : '0.0';
                const inMcrs = MCRS_STATUSES.includes(status);
                const inOor = OOR_STATUSES.includes(status);
                const catTotal = inMcrs ? mcrsTotal : inOor ? oorTotal : 0;
                const catPct = catTotal > 0 ? ((count / catTotal) * 100).toFixed(1) : null;
                const catName = inMcrs ? 'MCRS' : inOor ? 'OOR' : null;
                const isCollapsed = count === 0;

                return (
                    <div key={status} style={styles.statusSection}>
                        <div
                            style={{
                                ...(isCollapsed ? styles.statusHeaderCollapsed : styles.statusHeader),
                                backgroundColor: STATUS_COLORS[status] || '#6c757d',
                            }}
                        >
                            <div style={styles.statusHeaderLeft}>
                                <span style={styles.caret}>{isCollapsed ? '▸' : '▾'}</span>
                                <span>Total {status}</span>
                            </div>
                            <div>{count} of {totalAircraft} · {pct}%</div>
                            {catName ? (
                                <div>{catPct}% of {catName} ({catTotal})</div>
                            ) : (
                                <div style={{ visibility: 'hidden' }}>placeholder</div>
                            )}
                        </div>
                        {!isCollapsed && (
                            <div style={styles.aircraftGrid}>
                                {[...list]
                                    .sort((a, b) => (a.buno || '').localeCompare(b.buno || ''))
                                    .map((ac) => {
                                        const modex = getAircraftModex(ac);
                                        const tooltip = ac.last_fly_date
                                            ? `BUNO ${ac.buno} · LFD ${formatLfd(ac.last_fly_date)}`
                                            : `BUNO ${ac.buno || ''}`;
                                        return (
                                            <div key={ac.buno} style={aircraftItemStyle(ac, styles)} title={tooltip}>
                                                <div style={styles.buno}>{modex}</div>
                                                <div style={styles.lfd}>{formatLfd(ac.last_fly_date)}</div>
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </div>
                );
            })}

            <div style={{ ...styles.summaryRow, gridTemplateColumns: '1fr', marginTop: '24px', marginBottom: 0 }}>
                <div style={styles.summaryCard}>
                    <div style={styles.summaryTitle}>Readiness</div>
                    <div style={styles.summaryGrid}>
                        {[
                            { label: 'MCRS', value: mcrsTotal },
                            { label: 'OOR', value: oorTotal },
                            { label: 'XC', value: xcTotal },
                            { label: 'TAR', value: tarTotal },
                        ].map((item) => {
                            const pct = totalAircraft > 0 ? ((item.value / totalAircraft) * 100).toFixed(1) : '0.0';
                            return (
                                <div key={item.label} style={styles.summaryItem}>
                                    <div style={styles.summaryNumber}>{item.value}</div>
                                    <div style={styles.summaryPct}>{pct}%</div>
                                    <div style={styles.summaryLabel}>{item.label}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
}

export default TVDashboardPage;
